import { createMemo, Index, Show } from "solid-js";
import { streamingStore } from "../../../../store/streamingStore";
import { sourcesStore } from "../../../../store/sourcesStore";
import { defaultChannelsStore } from "../../../../store/defaultChannelsStore";
import { selectedTime, isPlaying, getDisplayWindowReferenceTime } from "../../../../store/playbackStore";
import { applyLowSpeedGradeRules, GRADE_INPUT_MAX_AGE_MS } from "../../../../utils/liveGradeRules";
import {
  hasHighlightedSources,
  isSourceHighlighted,
  toggleHighlightedSource,
} from "../../../../store/liveTrackHighlightStore";

/** Data overlay names that show the live BSP table (fleet map, live mode). Matches MapSettings combo value. */
export function isLiveDefaultDataOverlay(overlayName: string): boolean {
  const n = String(overlayName ?? "").trim().toLowerCase();
  return n === "default";
}

export interface LiveBspTableProps {
  /** Selected fleet sources (reactive accessor). */
  selectedSourceIds: () => Set<number>;
  /** Pre-filtered data from parent frame snapshot — avoids duplicate getFilteredData. */
  filteredDataMap?: () => Map<number, any[]> | undefined;
}

function getPointTimeMs(d: any): number {
  if (!d) return 0;
  const ts = d.timestamp ?? d.Datetime ?? d.time ?? d.datetime;
  if (ts instanceof Date) return ts.getTime();
  if (typeof ts === "number") return Number.isFinite(ts) ? ts : 0;
  if (typeof ts === "string") {
    const ms = new Date(ts).getTime();
    return Number.isFinite(ms) ? ms : 0;
  }
  return 0;
}

function readFiniteNumber(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** BSP for the value column: 0 means "no telemetry" here, not "stopped" — display it as missing. */
function readBsp(d: any, bspFieldName: string): number | null {
  const val = d[bspFieldName] ?? d.Bsp_kph ?? d.Bsp ?? d.bsp;
  const n = readFiniteNumber(val);
  return n !== null && n > 0 ? n : null;
}

/**
 * BSP for grading: any finite value, 0 and negatives included. A boat reporting a true 0 is the
 * clearest possible "not moving" evidence, so the grade path must not discard it the way the
 * display path does.
 */
function readBspForGrade(d: any, bspFieldName: string): number | null {
  const val = d[bspFieldName] ?? d.Bsp_kph ?? d.Bsp ?? d.bsp;
  return readFiniteNumber(val);
}

function readTwsKph(d: any): number | null {
  const val = d?.Tws_kph ?? d?.tws_kph ?? d?.Tws ?? d?.tws;
  return readFiniteNumber(val);
}

/** Realtime quality from processor only (`GRADE`). Maneuver review `Grade` is a different scale — do not mix in here. */
function readGrade(d: any): number | null {
  const val = d?.GRADE;
  return readFiniteNumber(val);
}

/** Last sample at or before playhead — matches LiveMapTimeSeries cursor / trailing edge of each series. */
export function bspAtOrBefore(sortedAsc: any[], playheadMs: number, bspField: string): number | null {
  if (!sortedAsc.length) return null;
  for (let i = sortedAsc.length - 1; i >= 0; i--) {
    const point = sortedAsc[i];
    const t = getPointTimeMs(point);
    if (Number.isFinite(playheadMs) && playheadMs > 0 && t > playheadMs) continue;
    const value = readBsp(point, bspField);
    if (value !== null) return value;
  }
  return null;
}

export function gradeAtOrBefore(sortedAsc: any[], playheadMs: number, bspField = "Bsp_kph"): number | null {
  if (!sortedAsc.length) return null;
  let latestTwsKph: number | null = null;
  let latestBspKph: number | null = null;
  let latestGrade: number | null = null;
  // Anchor the lookback on the newest sample at/before the playhead, then accept grade inputs only
  // from within GRADE_INPUT_MAX_AGE_MS of it — same bound the processor applies. Without it the
  // three values are collected independently with no time limit, so a current BSP could be graded
  // against a TWS from hours earlier.
  let anchorMs: number | null = null;

  for (let i = sortedAsc.length - 1; i >= 0; i--) {
    const point = sortedAsc[i];
    const t = getPointTimeMs(point);
    if (Number.isFinite(playheadMs) && playheadMs > 0 && t > playheadMs) continue;

    if (anchorMs === null) anchorMs = t;
    if (anchorMs - t > GRADE_INPUT_MAX_AGE_MS) break;

    latestTwsKph = latestTwsKph ?? readTwsKph(point);
    latestBspKph = latestBspKph ?? readBspForGrade(point, bspField);
    latestGrade = latestGrade ?? readGrade(point);

    if (latestTwsKph !== null && latestBspKph !== null && latestGrade !== null) {
      break;
    }
  }
  return applyLowSpeedGradeRules(latestGrade, latestBspKph, latestTwsKph);
}

function formatBsp(v: number | null): string {
  if (v == null) return "—";
  return v.toFixed(1);
}

function formatGrade(v: number | null): string {
  if (v == null) return "—";
  return String(Math.round(v));
}

function columnHeaderFromChannel(name: string): string {
  const n = name.trim() || "BSP";
  if (/kts/i.test(n)) return "BSP (kts)";
  if (/kph/i.test(n)) return "BSP (kph)";
  return n.replace(/_/g, " ");
}

/**
 * Live fleet map: table (source, BSP, Grade) using the same channel and playhead logic as LiveMapTimeSeries.
 * Re-renders only when streaming data or playhead changes (single memo over selected sources).
 */
export default function LiveBspTable(props: LiveBspTableProps) {
  const rows = createMemo(() => {
    const selected = props.selectedSourceIds();
    if (!selected.size || !sourcesStore.isReady()) return [];

    const parentMap = props.filteredDataMap?.();
    if (parentMap) {
      void streamingStore.getLiveDataAppendVersion()();
      if (isPlaying()) void getDisplayWindowReferenceTime();
    } else {
      const newDataMap = streamingStore.getNewData()();
      let _touchNew = 0;
      for (const [sid, pts] of newDataMap.entries()) {
        if (selected.has(sid) && pts?.length) _touchNew += pts.length;
      }
      void _touchNew;
      void streamingStore.getHistoricalDataVersion()();
      void streamingStore.getLiveDataAppendVersion()();
    }

    const bspField = defaultChannelsStore.bspName() || "Bsp_kph";
    const refTime = getDisplayWindowReferenceTime();
    const playhead = refTime instanceof Date && !isNaN(refTime.getTime()) ? refTime : selectedTime();
    const playheadMs = playhead instanceof Date && !isNaN(playhead.getTime()) ? playhead.getTime() : 0;
    const playing = isPlaying();

    const raw = parentMap ?? streamingStore.getRawData(selected);

    const sources = sourcesStore.sources();
    const ordered = [...selected].sort((a, b) => a - b);

    return ordered.map((sourceId) => {
      const meta = sources.find((s) => Number(s.source_id) === Number(sourceId));
      const sourceName = meta?.source_name?.trim() || `Source ${sourceId}`;
      const color = meta?.color || "#1f77b4";
      const data = raw.get(sourceId) || [];
      const sorted = [...data].sort((a, b) => getPointTimeMs(a) - getPointTimeMs(b));

      let slice = sorted;
      if (playing && playheadMs > 0) {
        slice = sorted.filter((d) => getPointTimeMs(d) <= playheadMs);
      }

      const bsp = bspAtOrBefore(slice, playheadMs, bspField);
      const grade = gradeAtOrBefore(slice, playheadMs, bspField);
      return { sourceId, sourceName, color, bsp, grade };
    });
  });

  const headerLabel = createMemo(() => columnHeaderFromChannel(defaultChannelsStore.bspName() || "BSP"));

  return (
    <div class="live-bsp-table" role="region" aria-label="Live boat speed and grade">
      <Show
        when={rows().length > 0}
        fallback={<p class="live-bsp-table__empty">No sources selected</p>}
      >
        <table class="live-bsp-table__grid">
          <thead>
            <tr>
              <th class="live-bsp-table__th live-bsp-table__th--source">Source</th>
              <th class="live-bsp-table__th live-bsp-table__th--value">{headerLabel()}</th>
              <th class="live-bsp-table__th live-bsp-table__th--grade" title="Realtime data grade (processor)">
                Grade
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Index, not For. `rows()` rebuilds its row objects on every playhead tick, and For
                keys by REFERENCE — so each tick disposed and recreated every <tr>, rebuilding the
                whole table ~1/s. That threw away hover state as you moved across it, which is what
                made the overlay flash. Index keys by position instead, so the nodes persist and
                only the values and classes update. The list is positional anyway: one row per
                selected source, sorted by id. */}
            <Index each={rows()}>
              {(row) => (
                <tr
                  class={`live-bsp-table__row${
                    isSourceHighlighted(row().sourceName) ? " live-bsp-table__row--highlighted" : ""
                  }${
                    hasHighlightedSources() && !isSourceHighlighted(row().sourceName)
                      ? " live-bsp-table__row--dimmed"
                      : ""
                  }`}
                  data-source-id={row().sourceId}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSourceHighlighted(row().sourceName)}
                  title={`Highlight ${row().sourceName} on the map`}
                  onClick={() => toggleHighlightedSource(row().sourceName)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleHighlightedSource(row().sourceName);
                    }
                  }}
                >
                  <td class="live-bsp-table__td live-bsp-table__td--source">
                    <div class="live-bsp-table__source-cell">
                      <span class="live-bsp-table__swatch" style={{ "--live-bsp-swatch": row().color }} />
                      <span class="live-bsp-table__name">{row().sourceName}</span>
                    </div>
                  </td>
                  <td class="live-bsp-table__td live-bsp-table__td--value">{formatBsp(row().bsp)}</td>
                  <td
                    class={`live-bsp-table__td live-bsp-table__td--grade ${
                      row().grade === 1
                        ? "live-bsp-table__grade--low"
                        : row().grade != null && row().grade >= 2
                          ? "live-bsp-table__grade--ok"
                          : ""
                    }`}
                  >
                    {formatGrade(row().grade)}
                  </td>
                </tr>
              )}
            </Index>
          </tbody>
        </table>
      </Show>
    </div>
  );
}
